import type { KeyboardEvent, MouseEventHandler, ReactNode } from 'react'
import { BottomSheet } from '../../Common/BottomSheet'
import { Button } from '../../Common/Button'
import { Pressable } from '../../Common/Pressable'

export const MemberHeroHeader = ({
  eyebrow,
  title,
  chips,
  className,
}: {
  eyebrow: ReactNode
  title: ReactNode
  chips?: ReactNode
  className?: string
}) => (
  <header className={['memberHeroHeader', className ?? ''].filter(Boolean).join(' ')}>
    <div className="memberHeroHeader__copy">
      <span className="memberEyebrow">{eyebrow}</span>
      <h2>{title}</h2>
    </div>
    {chips ? <div className="memberHeroHeader__chips">{chips}</div> : null}
  </header>
)

export type MemberSegmentedOption<T extends string> = {
  key: T
  label: ReactNode
  count?: number
  testId?: string
}

export const MemberSegmented = <T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: T
  options: Array<MemberSegmentedOption<T>>
  onChange: (value: T) => void
  ariaLabel: string
}) => (
  <div className="memberSegmented memberSegmented--premium" role="tablist" aria-label={ariaLabel}>
    {options.map((option) => (
      <Pressable
        key={option.key}
        role="tab"
        aria-selected={value === option.key}
        className={value === option.key ? 'isActive' : ''}
        onClick={() => onChange(option.key)}
        data-testid={option.testId}
      >
        <span>{option.label}</span>
        {typeof option.count === 'number' ? <span className="memberSegmentedCount">{option.count}</span> : null}
      </Pressable>
    ))}
  </div>
)

export type MemberActionCardTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info'

export const MemberActionCard = ({
  icon,
  actions,
  title,
  sub,
  status,
  statusTone = 'neutral',
  footer,
  className,
  testId,
  onClick,
  disabled,
}: {
  icon?: ReactNode
  actions?: ReactNode
  title: ReactNode
  sub?: ReactNode
  status?: ReactNode
  statusTone?: MemberActionCardTone
  footer?: ReactNode
  className?: string
  testId?: string
  onClick?: MouseEventHandler<HTMLElement>
  disabled?: boolean
}) => {
  const interactive = Boolean(onClick) && !disabled
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (!interactive) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      event.currentTarget.click()
    }
  }
  const cardClasses = `memberActionCard memberTapRipple${!icon ? ' memberActionCard--noIcon' : ''}${actions ? ' memberActionCard--hasActions' : ''} ${className ?? ''}`

  return (
  <article
    className={cardClasses}
    data-testid={testId}
    role={interactive ? 'button' : undefined}
    tabIndex={interactive ? 0 : undefined}
    aria-disabled={disabled ? 'true' : undefined}
    onClick={interactive ? onClick : undefined}
    onKeyDown={onKeyDown}
  >
    <span className="memberActionCard__rail" aria-hidden="true" />
    {icon ? <span className="memberActionCard__icon">{icon}</span> : null}
    <span className="memberActionCard__main">
      <span className="memberActionCard__top">
        <strong>{title}</strong>
        {status ? <span className={`memberStatusPill memberStatusPill--${statusTone}`}>{status}</span> : null}
      </span>
      {sub ? <small>{sub}</small> : null}
      {footer ? <span className="memberActionCard__footer">{footer}</span> : null}
    </span>
    {actions ? (
      <span
        className="memberActionCard__actions"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.stopPropagation()
          }
        }}
      >
        {actions}
      </span>
    ) : null}
  </article>
  )
}

export const MemberEmptyState = ({
  icon,
  title,
  hint,
  cta,
}: {
  icon: ReactNode
  title: ReactNode
  hint?: ReactNode
  cta?: ReactNode
}) => (
  <div className="memberEmptyState">
    <span className="memberEmptyState__icon">{icon}</span>
    <strong>{title}</strong>
    {hint ? <p>{hint}</p> : null}
    {cta ? <div className="memberEmptyState__cta">{cta}</div> : null}
  </div>
)

export const MemberBottomSheet = ({
  open,
  onClose,
  title,
  children,
  footer,
  maxHeightSvh = 94,
  testId,
}: {
  open: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  footer?: ReactNode
  maxHeightSvh?: number
  testId?: string
}) => (
  <BottomSheet
    isOpen={open}
    onClose={onClose}
    title={title}
    maxHeightSvh={maxHeightSvh}
    footer={footer}
    className="memberBottomSheet"
  >
    <div className="memberBottomSheet__body" data-member-shell-portal data-testid={testId}>
      {children}
    </div>
  </BottomSheet>
)

export const MemberConfirmSheet = ({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  tone = 'neutral',
  busy = false,
  onConfirm,
  onCancel,
  testId,
}: {
  open: boolean
  title: string
  body: ReactNode
  confirmLabel: string
  cancelLabel: string
  tone?: 'neutral' | 'danger'
  busy?: boolean
  onConfirm: () => void
  onCancel: () => void
  testId?: string
}) => {
  return (
    <MemberBottomSheet
      open={open}
      onClose={busy ? () => {} : onCancel}
      title={title}
      maxHeightSvh={40}
      testId={testId}
    >
      <div className="p-4 flex flex-col gap-4 text-[var(--text-strong)]" data-testid={testId ? `${testId}-content` : undefined}>
        <p className="text-sm text-[var(--muted)]">{body}</p>
        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            variant="tertiary"
            size="md"
            onClick={onCancel}
            disabled={busy}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            size="lg"
            onClick={onConfirm}
            disabled={busy}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </MemberBottomSheet>
  )
}

